import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';

import { errorText } from '../components/custom_creative_session/DevelopmentStage';
import { visibleForSession } from './sessionScope';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * The Creative Elements step's behaviour — ONE implementation, two screens.
 *
 * WAVE 3, SCREEN 6. This is the step where a script gets its creative type and
 * is submitted to production, and where loose hooks/bodies/CTAs can be
 * recombined into new creatives.
 *
 * THE DEFECT THE SWEEP FOUND, and it is live right now: the step scoped with
 * `scopeToSession` **strictly**. Measured: **3 of 288 scripts in production
 * carry a `session_id`.** So for 285 of them the scoped list is empty and this
 * step shows the operator NOTHING — no scripts to type, none to submit — while
 * the work sits on the offer.
 *
 * That is the same shape as the regression the Founder caught on wave 3 screen
 * 1, and the same one `visibleForSession` was written for: strict where the
 * rows are stamped, and falling back to the unstamped rows — saying so — rather
 * than showing an empty screen with the answer sitting in the response. Screens
 * 2, 3 and 4 were moved onto it as they were ported; this one had not been.
 *
 * The rest were the family: `alert()` on three paths, and no angle scoping, so
 * a multi-angle track saw every track's scripts.
 */
export function useCreativeElements({ offer, sessionId, isNewSession,
                                      sessionStartTime, angleId, onComplete,
                                      sessionMeta = null, refreshKey = 0 }) {
  // THE SESSION'S OWN CHOICE, as a fallback for a script that carries none.
  //
  // THE DEFECT, reported live 2026-09-09: the Founder chose AI VIDEO TRELLO on
  // step 1, reached this step, pressed "Submit all" and got
  // `Invalid creative type ''. Must be AI, VE, or UGC.` The session HAD the
  // answer — `creative_type: "AI:TRELLO"` — and this step never read it. A
  // script generated before the choice was made (or before that feature
  // deployed) carries no type of its own, and the batch fired a request that
  // could only fail.
  //
  // FOUNDER RULING 2026-09-09: the session's answer is now the ONLY answer —
  // step 6's dropdown is gone. `sessionMeta` is passed by v2's shell, which has
  // already read the session; the fetch below is the fallback for v1, which has
  // not. TWO READS OF THE SAME FIELD COULD DISAGREE, and the screen shows this
  // value in two places, so the passed one wins where it exists.
  const [fetchedChoice, setFetchedChoice] = useState(null);
  const sessionChoice = sessionMeta
    ? (sessionMeta.creative_type || null)
    : fetchedChoice;
  const [scripts, setScripts] = useState([]);
  const [hidden, setHidden] = useState({ hiddenOtherSession: 0, hiddenUnstamped: 0, predateNote: 0 });
  const [library, setLibrary] = useState({ HOOK: [], BODY: [], CTA: [] });

  const [loadState, setLoadState] = useState('idle');
  const [loadError, setLoadError] = useState(null);
  const [libraryState, setLibraryState] = useState('idle');
  const [libraryError, setLibraryError] = useState(null);

  const [actionError, setActionError] = useState(null);
  const [note, setNote] = useState(null);
  const [busy, setBusy] = useState(null);

  // WHAT THE PROMPTS LOOK LIKE, FOR EVERY SCRIPT, IN ONE PRESS.
  //
  // THE FOUNDER, 2026-09-10: "I hit submit all but only had clicked show the
  // video prompts on the first one, I wanted to ensure that both prompts existed
  // without having to actually click on both."
  //
  // `{ [script_id]: { state, clips, short, error } }`. Absent means NOT CHECKED,
  // which is deliberately different from checked-and-fine: a blank row must not
  // read as a pass.
  const [promptChecks, setPromptChecks] = useState({});
  const [checking, setChecking] = useState(false);

  const load = useCallback(async () => {
    setLoadState('loading');
    try {
      const res = await axios.get(`${API}/offers/${offer.offer_id}/scripts`,
                                  angleId ? { params: { angle_id: angleId } } : undefined);
      const view = visibleForSession(res.data.scripts || [], sessionId,
                                     { isNewSession, sessionStartTime });
      setScripts(view.rows);
      setHidden({
        hiddenOtherSession: view.hiddenOtherSession,
        hiddenUnstamped: view.hiddenUnstamped,
        predateNote: view.predateNote,
      });
      setLoadState('ok');
      setLoadError(null);
    } catch (error) {
      setLoadState('failed');
      setLoadError(errorText(error));
    }
  }, [offer.offer_id, sessionId, isNewSession, sessionStartTime, angleId]);

  /** The loose element library the assembly panel recombines. */
  const loadLibrary = useCallback(async () => {
    setLibraryState('loading');
    try {
      const get = (type) => axios.get(`${API}/copy-elements`, {
        params: { offer_id: offer.offer_id, element_type: type, limit: 500 },
      });
      const [h, b, c] = await Promise.all([get('HOOK'), get('BODY'), get('CTA')]);
      const rows = (r) => r.data.elements || r.data.copy_elements || [];
      setLibrary({ HOOK: rows(h), BODY: rows(b), CTA: rows(c) });
      setLibraryState('ok');
      setLibraryError(null);
    } catch (error) {
      // A failed library read and an offer with no elements are different
      // facts, and the panel must not offer empty pickers as if they were a
      // choice.
      setLibraryState('failed');
      setLibraryError(errorText(error));
    }
  }, [offer.offer_id]);

  useEffect(() => {
    if (!sessionId || sessionMeta) return undefined;
    let live = true;
    axios.get(`${API}/creative/sessions/${sessionId}`)
      .then((r) => { if (live) setFetchedChoice((r.data?.session || r.data)?.creative_type || null); })
      // Not fatal, and it must not take the whole step down. What it costs is
      // the inherited type: a script with none of its own then has no type
      // here, and `submitAll` refuses it by name rather than sending an empty
      // one — which is the outcome this whole path exists to produce.
      .catch(() => { if (live) setFetchedChoice(null); });
    return () => { live = false; };
  }, [sessionId, sessionMeta, refreshKey]);

  useEffect(() => { load(); }, [load, refreshKey]);
  useEffect(() => { loadLibrary(); }, [loadLibrary, refreshKey]);

  const setCreativeType = async (scriptId, value) => {
    const [type, destination] = String(value).split(':');
    setBusy(scriptId);
    setActionError(null);
    try {
      const body = { creative_type: type };
      if (destination) body.production_destination = destination;
      await axios.patch(`${API}/scripts/${scriptId}/creative-type`, body);
      setScripts((prev) => prev.map((s) => (s.script_id === scriptId
        ? { ...s, creative_type: type, production_destination: destination || null }
        : s)));
      return true;
    } catch (error) {
      setActionError(errorText(error));
      return false;
    } finally {
      setBusy(null);
    }
  };

  const routeToProduction = async (scriptId, optionValue) => {
    setBusy(scriptId);
    setActionError(null);
    // The caller passes the option VALUE ("AI:TRELLO"); the script's field takes
    // only the bare type. Splitting it here rather than at four call sites.
    const [type, destination] = String(optionValue || "").split(":");
    if (!type) {
      setActionError("This script has no creative type, and neither does this "
                   + "session. It is chosen on step 1.");
      setBusy(null);
      return false;
    }
    try {
      await axios.patch(`${API}/scripts/${scriptId}/creative-type`,
                        destination
                          ? { creative_type: type, production_destination: destination }
                          : { creative_type: type });
      await axios.post(`${API}/scripts/${scriptId}/route-to-production`);
      await load();
      return true;
    } catch (error) {
      setActionError(errorText(error));
      return false;
    } finally {
      setBusy(null);
    }
  };

  /**
   * Submit every unrouted script, and report per script.
   *
   * Returns `{ routed, failures }` instead of raising an `alert()`. A batch
   * where three succeeded and one failed is not "done" and not "failed" — the
   * screen has to be able to say which one did not go, and why.
   */
  const submitAll = async (unrouted) => {
    setBusy('__all__');
    setActionError(null);
    let routed = 0;
    const failures = [];
    for (const script of unrouted) {
      const label = script.body_element_number || String(script.script_id).slice(0, 10);
      const [type, destination] = String(effectiveType(script)).split(":");
      // REFUSED HERE, not by the server. A request that can only come back
      // "Invalid creative type ''" should never be sent — and the operator
      // needs to know WHICH script has no type, not that a batch failed.
      if (!type) {
        failures.push({
          label,
          detail: "no creative type — it is chosen once, on step 1, and this "
                + "session has not chosen one",
        });
        continue;
      }
      try {
        await axios.patch(`${API}/scripts/${script.script_id}/creative-type`,
                          destination
                            ? { creative_type: type, production_destination: destination }
                            : { creative_type: type });
        await axios.post(`${API}/scripts/${script.script_id}/route-to-production`);
        routed += 1;
      } catch (error) {
        failures.push({ label, detail: errorText(error) });
      }
    }
    if (routed) await load();
    setBusy(null);
    setNote(
      failures.length === 0
        ? `Submitted ${routed} script${routed === 1 ? '' : 's'} to production.`
        : routed === 0
          ? `Nothing was submitted. ${failures.length} script(s) were refused.`
          : `Submitted ${routed}; ${failures.length} refused.`);
    return { routed, failures };
  };

  /**
   * One body + one CTA, tested against every selected hook.
   *
   * Reports the same way `submitAll` does: partial success is its own outcome.
   */
  const assemble = async ({ bodyId, ctaId, hookIds, channel, creativeType }) => {
    if (!bodyId || !ctaId || !hookIds?.length) {
      setActionError('Pick a body, a CTA, and at least one hook to test.');
      return { created: [], failures: [] };
    }
    setBusy('__assembly__');
    setActionError(null);
    const created = [];
    const failures = [];
    for (const hookId of hookIds) {
      try {
        const res = await axios.post(`${API}/offers/${offer.offer_id}/creatives/combine`, {
          hook_element_id: hookId, body_element_id: bodyId, cta_element_id: ctaId,
          channel, creative_type: creativeType,
        });
        created.push(res.data);
      } catch (error) {
        failures.push({ hookId, detail: errorText(error) });
      }
    }
    setBusy(null);
    setNote(
      failures.length === 0
        ? `Assembled ${created.length} creative${created.length === 1 ? '' : 's'}.`
        : created.length === 0
          ? `Nothing was assembled. All ${failures.length} attempt(s) failed.`
          : `Assembled ${created.length}; ${failures.length} failed.`);
    return { created, failures };
  };

  /**
   * Build the prompts for every AI script in view and report per script.
   *
   * It is a real model call per script, so it is a button and never automatic,
   * and the screen says what it costs. Nothing is saved and nothing is
   * submitted — same read the row's own panel uses.
   */
  const checkAllPrompts = async () => {
    const ai = scripts.filter((s) => s.creative_type === 'AI');
    if (ai.length === 0) return { checked: 0 };
    setChecking(true);
    setActionError(null);
    for (const script of ai) {
      try {
        const res = await axios.post(
          `${API}/scripts/${script.script_id}/ai-video-prompts/preview`);
        const segs = res.data?.segments || [];
        setPromptChecks((prev) => ({
          ...prev,
          [script.script_id]: {
            state: res.data?.state,
            clips: segs.length,
            // Counted from the rows themselves rather than trusting the state:
            // a truncated clip is the specific thing worth seeing here.
            short: segs.filter((x) => x.looks_truncated).length,
            empty: segs.filter((x) => !(x.translated_veo_prompt || '').trim()).length,
            warnings: res.data?.warnings || [],
            error: res.data?.error || null,
          },
        }));
      } catch (error) {
        // A failed CHECK is not a script with no prompts.
        setPromptChecks((prev) => ({
          ...prev,
          [script.script_id]: { state: 'check_failed', error: errorText(error) },
        }));
      }
    }
    setChecking(false);
    return { checked: ai.length };
  };

  /** What this script will actually be built as: its own type, else the
   *  session's. Returned as the option VALUE ("AI:TRELLO"), which is what the
   *  dropdown and the PATCH both speak. */
  const effectiveType = (script) => {
    if (script.creative_type) {
      return script.production_destination
        ? `${script.creative_type}:${script.production_destination}`
        : script.creative_type;
    }
    return sessionChoice || "";
  };

  const unrouted = scripts.filter((s) => !s.production_id);

  return {
    scripts, unrouted, library,
    ...hidden,
    loadState, loadError, retryLoad: load,
    libraryState, libraryError, retryLibrary: loadLibrary,
    busy, setCreativeType, routeToProduction, submitAll, assemble,
    sessionChoice, effectiveType,
    promptChecks, checking, checkAllPrompts,
    actionError, setActionError, note,
    proceed: onComplete,
  };
}
